import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { ShiftDetailsErrorPage } from "@src/appV2/redesign/WorkerShifts/components/ShiftDetailsErrorPage";
import {
  SHIFT_DETAILS_ATTENDANCE_CONFIRMED_PATH,
  SHIFT_DETAILS_CANCEL_SHIFT_PATH,
  SHIFT_DETAILS_CONFIRM_ATTENDANCE_PATH,
  SHIFT_DETAILS_EC_CLOCK_OUT_FLOW_PATH,
  SHIFT_DETAILS_LOCATION_TRACKING_FAILED_PATH,
  SHIFT_DETAILS_OPEN_GEOLOCATION_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
  SHIFT_DETAILS_TIMEKEEPING_PATH,
  SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_PATH,
  SHIFT_DETAILS_UPDATE_ETA_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { ShiftDetailsLoadingSkeleton } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/ShiftDetailsLoadingSkeleton";
import { ShiftDetailsPage } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/ShiftDetailsPage";
import { ShiftTimekeepingPage } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/Timekeeping/Page";
import { Redirect, Route, Switch, useLocation, useParams, useRouteMatch } from "react-router-dom";

import { CancelShiftPage } from "./CancelShift/CancelShiftPage";
import { LocationTrackingFailedPage } from "./LocationTrackingFailed/LocationTrackingFailedPage";
import { EmergencyCreditsClockOutFlowPage } from "./Timekeeping/EmergencyCreditsClockOutFlow/Page";
import { SubmitTimesheetPage } from "./Timekeeping/SubmitTimesheetPage";

// eslint-disable-next-line complexity
export function ShiftDetailsRoute() {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const { shiftId } = useParams<{ shiftId: string }>();

  const { data: shiftResponse, isLoading, isError } = useGetWorkerShift(shiftId);

  if (isLoading) {
    return <ShiftDetailsLoadingSkeleton />;
  }

  if (isError) {
    return <ShiftDetailsErrorPage />;
  }

  const isClockedIn = isDefined(shiftResponse.response.clockInOut?.start);
  const isClockedOut = isDefined(shiftResponse.response.clockInOut?.end);
  const isInstantPayShift = shiftResponse.response.isInstantPay;

  const relativePath = location.pathname.replace(url, "") || "/";
  const isOnShiftDetailsPage = relativePath === "/";
  const isOnTimekeepingPage = relativePath === SHIFT_DETAILS_TIMEKEEPING_PATH;
  const isOnSubmitTimesheetPage = relativePath === SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH;
  const isOnEtaModalPage = relativePath === SHIFT_DETAILS_UPDATE_ETA_PATH;
  const isOnEtaConfirmedModalPage = relativePath === SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_PATH;
  const isOnConfirmAttendanceModalPage = relativePath === SHIFT_DETAILS_CONFIRM_ATTENDANCE_PATH;
  const isOnAttendanceConfirmedModalPage = relativePath === SHIFT_DETAILS_ATTENDANCE_CONFIRMED_PATH;
  const isOnOpenGeoLocationModalPage = relativePath === SHIFT_DETAILS_OPEN_GEOLOCATION_PATH;
  const isOnECClockOutFlowPage = relativePath === SHIFT_DETAILS_EC_CLOCK_OUT_FLOW_PATH;
  // TODO The CancelShiftPage will need to go to a different route. See SYNF-168.
  const isOnCancelShiftPage = relativePath === SHIFT_DETAILS_CANCEL_SHIFT_PATH;
  const isOnLocationTrackingFailedPage =
    relativePath === SHIFT_DETAILS_LOCATION_TRACKING_FAILED_PATH;

  const shiftDetailsUrl = url;
  const shiftTimekeepingUrl = `${shiftDetailsUrl}${SHIFT_DETAILS_TIMEKEEPING_PATH}`;
  const submitTimesheetUrl = `${shiftDetailsUrl}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  // Non-instant pay shifts can only access the shift details page and cancel shift page
  if (!isInstantPayShift && !isOnShiftDetailsPage && !isOnCancelShiftPage) {
    return <Redirect to={{ pathname: shiftDetailsUrl, state: location.state }} />;
  }

  // Before clocking in, workers can only access shift details and attendance/ETA modals
  const isOnAllowedPreClockInPage =
    isOnShiftDetailsPage ||
    isOnEtaModalPage ||
    isOnEtaConfirmedModalPage ||
    isOnConfirmAttendanceModalPage ||
    isOnAttendanceConfirmedModalPage ||
    isOnOpenGeoLocationModalPage ||
    isOnCancelShiftPage ||
    isOnLocationTrackingFailedPage;

  if (!isClockedIn && !isOnAllowedPreClockInPage) {
    return <Redirect to={{ pathname: shiftDetailsUrl, state: location.state }} />;
  }

  // Active instant pay shifts (clocked in, not out) should be on timekeeping page
  if (isInstantPayShift && isClockedIn && !isClockedOut && !isOnTimekeepingPage) {
    return <Redirect to={{ pathname: shiftTimekeepingUrl, state: location.state }} />;
  }

  // Completed instant pay shifts should be on submit timesheet page or EC clock-out flow page
  if (
    isInstantPayShift &&
    isClockedIn &&
    isClockedOut &&
    !isOnSubmitTimesheetPage &&
    !isOnECClockOutFlowPage
  ) {
    return <Redirect to={{ pathname: submitTimesheetUrl, state: location.state }} />;
  }

  return (
    <Switch>
      <Route exact path={`${path}${SHIFT_DETAILS_EC_CLOCK_OUT_FLOW_PATH}`}>
        <EmergencyCreditsClockOutFlowPage />
      </Route>
      <Route exact path={`${path}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`}>
        <SubmitTimesheetPage />
      </Route>
      <Route exact path={`${path}${SHIFT_DETAILS_TIMEKEEPING_PATH}`}>
        <ShiftTimekeepingPage />
      </Route>
      <Route exact path={`${path}${SHIFT_DETAILS_CANCEL_SHIFT_PATH}`}>
        <CancelShiftPage />
      </Route>
      <Route exact path={`${path}${SHIFT_DETAILS_LOCATION_TRACKING_FAILED_PATH}`}>
        <LocationTrackingFailedPage />
      </Route>
      <Route path={path}>
        <ShiftDetailsPage />
      </Route>
    </Switch>
  );
}
